// ===========================================
// Registration fees kept, by financial year (#374 P8)
// ===========================================
// What the school kept when a child did not proceed (Not proceeding), net of undone and Stripe-
// refunded fees, for one financial year. Every movement sits on its OWN day, so a past year never
// changes. A fee Stripe refunded is listed but kept OUT of the Kept total; a partial refund keeps
// the rest. The first load sends no year (the server's current one); after that the select sends
// the chosen year. Movement words mirror the CSV (REGISTRATION_FEE_FORFEIT_MOVEMENT_LABELS, pinned
// by a parity test). Gated by registration_fees.read.

import { useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { usePermissions } from '../../../hooks/usePermissions';
import { useExportRegistrationFeesForfeited, useRegistrationFeesForfeited } from '../../../hooks/useReports';
import type { ForfeitMovementKind, ForfeitMovementRow } from '../../../api/reports.api';
import { formatCurrencyFromDollars } from '../../../utils/currency';
import { apiErrorMessage } from '../../../utils/apiError';

/** Mirrors the CSV export's words exactly (backend REGISTRATION_FEE_FORFEIT_MOVEMENT_LABELS). */
const MOVEMENT_LABELS: Record<ForfeitMovementKind, string> = {
  FORFEITED: 'Not proceeding',
  REVERSED: 'Undone',
  REFUNDED: 'Refunded (Stripe)',
};

const MOVEMENT_COLOR: Record<ForfeitMovementKind, 'default' | 'success' | 'warning'> = {
  FORFEITED: 'success',
  REVERSED: 'default',
  REFUNDED: 'warning',
};

const nowrap = { whiteSpace: 'nowrap' } as const;

/** A school-day key (YYYY-MM-DD) as dd/mm/yyyy, without a Date object. */
const dayKeyToAu = (key: string) => key.split('-').reverse().join('/');

/** A signed money string: a negative shows with a minus sign ("−$48.50"). */
function signedMoney(v: string): string {
  return v.startsWith('-') ? `−${formatCurrencyFromDollars(v.slice(1))}` : formatCurrencyFromDollars(v);
}

export default function RegistrationFeesForfeitedPage() {
  const { schoolSlug } = useParams<{ schoolSlug: string }>();
  const { can } = usePermissions();
  const canRead = can('registration_fees', 'read');
  // undefined = the server's current financial year (first load)
  const [fy, setFy] = useState<number | undefined>(undefined);
  const { data, isLoading, isError, error, isPlaceholderData, refetch } = useRegistrationFeesForfeited(
    fy === undefined ? {} : { fy },
    canRead
  );
  const exportMutation = useExportRegistrationFeesForfeited();
  // The last years list that loaded: the select stays usable when another year fails (/qa P8 L6).
  const [lastYears, setLastYears] = useState<{ fy: number; label: string }[]>([]);
  const dataYears = data?.years;
  useEffect(() => {
    if (dataYears) setLastYears(dataYears);
  }, [dataYears]);
  const years = dataYears ?? lastYears;

  if (!canRead) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">You do not have permission to open the registration fees report.</Alert>
      </Box>
    );
  }

  const base = `/${schoolSlug}/admin`;
  // The previous year's data held over while another loads is not shown as this year's.
  const shown = data && !isPlaceholderData ? data : null;

  const familyCell = (r: ForfeitMovementRow) =>
    r.familyName === null ? (
      <span />
    ) : r.parentId ? (
      <Link component={RouterLink} to={`${base}/families?parent=${encodeURIComponent(r.parentId)}`}>
        {r.familyName}
      </Link>
    ) : (
      <span>{r.familyName}</span>
    );

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h4" gutterBottom>
            Registration fees kept
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Fees the school kept when a child did not proceed. Every movement is on its own day, so a past year never
            changes. A fee Stripe refunded is listed but kept out of the Kept total; a partial refund keeps the rest.{' '}
            <Link component={RouterLink} to={`${base}/reports/registration-fees`}>
              Fees not yet applied
            </Link>
            {' · '}
            <Link component={RouterLink} to={`${base}/reports/registration-fees/weekly`}>
              Fees paid by week
            </Link>
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={exportMutation.isPending ? <CircularProgress size={16} /> : <DownloadIcon />}
          onClick={() => shown && exportMutation.mutate({ fy: shown.fy })}
          disabled={!shown || shown.rows.length === 0 || exportMutation.isPending}
          sx={{ minHeight: { xs: 44 } }}
        >
          Export CSV
        </Button>
      </Stack>

      {years.length > 0 && (
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <TextField
            select
            size="small"
            label="Financial year"
            value={String(fy ?? data?.fy ?? years[0].fy)}
            onChange={(e) => setFy(Number(e.target.value))}
            SelectProps={{ native: true }}
            sx={{ minWidth: 220 }}
          >
            {years.map((y) => (
              <option key={y.fy} value={String(y.fy)}>
                {y.label}
              </option>
            ))}
          </TextField>
          {data && !isPlaceholderData && (
            <Typography variant="subtitle1" data-testid="period-label">
              {dayKeyToAu(data.period.from)} to {dayKeyToAu(data.period.to)}
            </Typography>
          )}
        </Stack>
      )}

      {isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          {apiErrorMessage(error, 'Could not load the registration fees kept report.')}
        </Alert>
      ) : isLoading || !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={2}>
          {isPlaceholderData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1 }} data-testid="kept-total">
                  Kept {formatCurrencyFromDollars(data.totals.keptGross)} · Taken back {formatCurrencyFromDollars(data.totals.takenBack)} · Net kept{' '}
                  <strong>{signedMoney(data.totals.kept)}</strong>
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }} data-testid="forfeited-totals">
                  <Chip size="small" variant="outlined" label={`Left out for Stripe refunds: ${formatCurrencyFromDollars(data.totals.refunded)}`} />
                  <Chip size="small" variant="outlined" label={`Marked Not proceeding: ${data.totals.forfeited}`} />
                  <Chip size="small" variant="outlined" label={`Undone: ${data.totals.reversed}`} />
                </Stack>

                {data.rows.length === 0 ? (
                  <Alert severity="info">Nothing was kept or undone in this financial year.</Alert>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={nowrap}>Day</TableCell>
                          <TableCell>Child</TableCell>
                          <TableCell>Family</TableCell>
                          <TableCell>Movement</TableCell>
                          <TableCell>Reason</TableCell>
                          <TableCell align="right">Fee</TableCell>
                          <TableCell align="right">Refunded</TableCell>
                          <TableCell align="right">Kept</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.rows.map((r) => (
                          <TableRow key={r.id} data-testid="forfeited-row">
                            <TableCell sx={nowrap}>{dayKeyToAu(r.day)}</TableCell>
                            <TableCell sx={nowrap}>{r.childName ?? ''}</TableCell>
                            <TableCell sx={nowrap}>{familyCell(r)}</TableCell>
                            <TableCell>
                              <Chip size="small" color={MOVEMENT_COLOR[r.kind]} label={MOVEMENT_LABELS[r.kind]} />
                            </TableCell>
                            <TableCell>{r.reason ? (r.kind === 'REVERSED' ? `Was: ${r.reason}` : r.reason) : ''}</TableCell>
                            <TableCell align="right" sx={nowrap}>{r.feeAmount ? signedMoney(r.feeAmount) : ''}</TableCell>
                            <TableCell align="right" sx={nowrap}>{r.refunded ? signedMoney(r.refunded) : ''}</TableCell>
                            <TableCell align="right" sx={nowrap}>{signedMoney(r.kept)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          )}
        </Stack>
      )}
    </Box>
  );
}
